import { Source } from "../types/chat";
import { ExternalLink, FileText } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useEffect, useRef, useState } from "react";
import { getCitedPage } from "../utils/source-citations";
import { useOpenSourceFile } from "../utils/use-open-source-file";

interface SourceFileProps {
    sourceFile: Source;
    /** DOM id so an inline citation marker can scroll this pill into view. */
    domId?: string;
}

export default function SourceFile({ sourceFile, domId }: SourceFileProps) {
    const { openSourceFile, loading } = useOpenSourceFile();
    const textRef = useRef<HTMLSpanElement>(null);
    const [isTextOverflowing, setIsTextOverflowing] = useState(false);

    useEffect(() => {
        const checkOverflow = () => {
            if (textRef.current) {
                const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
                setIsTextOverflowing(isOverflowing);
            }
        };

        checkOverflow();
        window.addEventListener('resize', checkOverflow);

        return () => {
            window.removeEventListener('resize', checkOverflow);
        };
    }, []);

    return (
        <button
            id={domId}
            key={sourceFile.fileId}
            className="flex h-[18px] items-center justify-between bg-gray-200 gap-1 p-2 rounded-full cursor-pointer transition-shadow"
            onClick={() => openSourceFile(sourceFile, getCitedPage(sourceFile))}
            disabled={loading}
        >
            <FileText className="h-[10px] w-[10px] text-gray-500" />
            {isTextOverflowing ? (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span ref={textRef} className="text-[8px] text-black truncate max-w-[100px]">
                                {sourceFile.fileName}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="text-xs text-gray-50 max-w-[200px] truncate">{sourceFile.fileName}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ) : (
                <span ref={textRef} className="text-[8px] text-black truncate max-w-[100px]">
                    {sourceFile.fileName}
                </span>
            )}
            <ExternalLink className="h-[10px] w-[10px] text-gray-500" />
        </button>
    );
}
